<template>
  <FriedModal class="upload-file-modal" :closable="false">
    <template #header>
      <FriedModalHeader :header="t('files_upload_files')" />
    </template>
    <div class="upload-container">
      <Dropzone
        :class="['dropzone-container', { 'has-files': hasFiles }]"
        :is-upload-restricted="isUploadRestricted"
        :file-size-limit="fileSizeLimit"
        @add-file="handleAddFile"
      />
    </div>
    <div class="file-info-container">
      <template v-if="filesToUpload.length > 0">
        <FriedH4>{{ t('files_ready_for_upload') }}</FriedH4>
        <div
          v-for="(item, index) in filesToUpload"
          :key="`file-${index}`"
          class="file-names-container"
        >
          <div class="placeholder file-name-input" :data-placeholder="item.extension">
            <FriedInput
              v-model="item.fileName"
              :error-message="getErrorMessage(item.fileName)"
              :label="t('files_file_name')"
              class="file-name-input"
              :data-external="`file-${index}-name-input`"
            />
          </div>
          <FriedIconButton
            class="delete-file-button"
            :data-external="`file-${index}-delete-button`"
            @click="removeFile(item.file)"
          >
            <FriedIcon icon="delete-bin" />
          </FriedIconButton>
        </div>
      </template>
      <template v-if="invalidFiles.length > 0">
        <FriedH4>{{ t('files_can_not_be_updated') }}</FriedH4>
        <FriedParagraph
          v-for="(file, index) in invalidFiles"
          :key="`invalid-file-${index}`"
          v-tooltip="{
            content: file.error,
            placement: Placement.TopStart,
            boundary: getTooltipBoundaryElement(),
            delay: { show: 300, hide: 100 },
            distance: 8,
          }"
          class="failed-file-name"
        >
          {{ file.name }}
        </FriedParagraph>
      </template>
    </div>
    <template #footer>
      <div class="button-container">
        <FriedButton
          :button-type="ButtonType.Secondary"
          data-external="upload-files-modal-cancel-button"
          @click="$emit('close')"
          >{{ t('cancel') }}</FriedButton
        >
        <FriedButton
          :disabled="filesToUpload.length === 0 || hasEmptyFileNames"
          data-external="upload-files-modal-upload-button"
          @click="uploadFiles"
          >{{ t('upload') }}</FriedButton
        >
      </div>
    </template>
  </FriedModal>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { ButtonType, Placement } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { FileHelper } from '@getaccept/lib-shared/src/file/helpers/file.helper';
import type { UploadFilePayload } from '../types/upload-file-payload';
import type { InvalidFile } from '../types/dealroom-file';
import Dropzone from './Dropzone.vue';

export default defineComponent({
  components: {
    Dropzone,
  },
  props: {
    isUploadRestricted: {
      type: Boolean,
    },
    fileSizeLimit: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close', 'upload-files'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const filesToUpload = ref<UploadFilePayload[]>([]);
    const invalidFiles = ref<InvalidFile[]>([]);

    const validateFile = (file: File) => {
      if (props.isUploadRestricted && file.size > props.fileSizeLimit) {
        return t('files_filesize_too_large_tooltip');
      }
      const fileType = FileHelper.extractFileType(file.name);
      if (!FileHelper.validFileType(fileType)) {
        return t('files_not_supported');
      }
      return null;
    };

    const handleFileToBeUploaded = (file: File) => {
      const [fileName, extension] = file.name.split(/(?=\.[^.]+$)/);
      filesToUpload.value.push({ file, fileName, extension });
    };

    const handleAddFile = (file: File) => {
      const error = validateFile(file);
      if (!error) {
        handleFileToBeUploaded(file);
        return;
      }
      invalidFiles.value.push({ name: file.name, error });
    };

    const removeFile = (file: File) => {
      filesToUpload.value = filesToUpload.value.filter(fileToUpload => fileToUpload.file !== file);
    };

    const uploadFiles = () => {
      const files = filesToUpload.value.map(fileToUpload => {
        const { fileName, extension, file } = fileToUpload;
        return new File([file], `${fileName}${extension}`, {
          type: file.type,
        });
      });
      emit('upload-files', files);
    };

    const hasFiles = computed(
      () => filesToUpload.value.length > 0 || invalidFiles.value.length > 0
    );

    const hasEmptyFileNames = computed(
      () => filesToUpload.value.filter(file => !file.fileName).length > 0
    );

    const getErrorMessage = (fileName: string) => (!fileName ? t('files_file_name_required') : '');

    const getTooltipBoundaryElement = () => document.querySelector('.upload-file-modal');

    return {
      filesToUpload,
      invalidFiles,
      hasFiles,
      hasEmptyFileNames,

      handleAddFile,
      uploadFiles,
      handleFileToBeUploaded,
      getErrorMessage,
      removeFile,
      getTooltipBoundaryElement,

      t,
      ButtonType,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
$full-height-dropzone: 350px;
$compact-dropzone: 200px;

.upload-file-modal {
  .upload-container {
    margin-bottom: var(--spacing-100);

    @media screen and (max-width: $sm) {
      margin-bottom: var(--spacing-50);
    }
  }

  .dropzone-container {
    height: $full-height-dropzone;

    &.has-files {
      height: $compact-dropzone;
    }
  }

  .file-info-container {
    max-height: 300px;
    overflow-y: auto;

    .heading-4 {
      margin-bottom: var(--spacing-50);
    }

    .file-names-container {
      display: flex;
      gap: var(--spacing-50);
      padding-bottom: var(--spacing-50);
      align-items: center;

      .file-name-input {
        width: 100%;
      }

      .delete-file-button {
        margin-top: var(--spacing-50);
      }
    }

    .placeholder {
      position: relative;
      display: inline-block;
    }

    .placeholder::after {
      position: absolute;
      right: var(--spacing-50);
      top: 18px;
      content: attr(data-placeholder);
      pointer-events: none;
      opacity: 0.6;
    }
  }

  .button-container {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-100);

    @media screen and (max-width: $sm) {
      flex-direction: column-reverse;
    }
  }

  .failed-file-name {
    color: var(--red-42);
    width: fit-content;
  }
}
</style>
